import React, { useState, useEffect } from "react";
import EAModal from "../../shared/Modal/EAModal";
import { useTranslation } from "react-i18next";
import EADivider from "../../shared/EADivider";
import { AssortGroup } from "../../../models/AssortGroup";
import EAButton from "../../shared/Buttons/EAButton";
import { BsBasket3 } from "react-icons/bs";
import useFacility from "../../../../utils/hooks/useFacility";
import ClubPassRulesMobile from "../ClubPassRules";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import ClubPassRulesDesktop from "../ClubPassRules/ClubPassRulesDesktop";
import CalendarPassMobile from "../CalendarPass";
import CalendarPassDesktop from "../CalendarPass/CalendarPassDesktop";
import useClubCart from "../ClubPassRules/hook/useClubCart";
import EACheckbox from "../../shared/Checkbox/EACheckbox";
import EAAlert from "../../shared/EAAlert";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  pass: AssortGroup;
  selectedDate: Date;
  setDateBetween: (date: Date) => void;
  onAddToBasket: () => void;
  errorMessage?: string;
}

const PassModal: React.FC<IProps> = ({ isOpen, onClose, pass, selectedDate, setDateBetween, onAddToBasket, errorMessage }) => {
  const [clubCartAgreement, setClubCartAgreement] = useState(false);
  const [continuityAgreement, setContinuityAgreement] = useState(false);
  const facilityIds = useFacility();

  const { getClubCartDetails, clubCarnetDetails, loading: clubCartLoading } = useClubCart();

  useEffect(() => {
    if (pass.passtype === 8 && isOpen) {
      getClubCartDetails(`0,${pass.objectId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass.passtype, pass.objectId, isOpen]);

  // Reset checkboxów gdy modal się zamyka
  useEffect(() => {
    if (!isOpen) {
      setClubCartAgreement(false);
      setContinuityAgreement(false);
    }
  }, [isOpen]);

  const handleCheckboxChange = (checked: boolean) => {
    setClubCartAgreement(checked);
  };

  const handleContinuityCheckboxChange = (checked: boolean) => {
    setContinuityAgreement(checked);
  };

  const { t } = useTranslation();
  const [readMore, setReadMore] = useState(false);
  const isDesktop = useDesktopSize();

  const hasContinuityIssue =
    pass.passtype === 8 &&
    !clubCartLoading &&
    clubCarnetDetails &&
    clubCarnetDetails.first === false &&
    (clubCarnetDetails.daysBetween ?? 0) > 0;

  // Obliczenie liczby dni do wyświetlenia (maksymalnie 30)
  const daysToDisplay =
    hasContinuityIssue && clubCarnetDetails?.daysBetween ? Math.min(clubCarnetDetails.daysBetween, 30) : 0;

  return (
    <EAModal isOpen={isOpen} title={pass.displayWebName || pass.name} onClose={onClose} hideButtons>
      <div className="pass-modal-container">
        <div className="pass-modal-block pass-modal-product-info">
          {pass.picture && <img alt="Miniatura zdjęcia produktu" src={pass.picture} />}

          {isDesktop && <h4>{pass.displayWebName || pass.name}</h4> }

          <p
            className="pass-modal-product-info__description"
            dangerouslySetInnerHTML={{ __html: pass.description }}
          ></p>

          {pass.description.length > 6 && (
            <EAButton variant="outlined" title={t("Czytaj więcej")} fullWidth onClick={() => setReadMore(true)} />
          )}
        </div>

        <div className="pass-modal-block pass-modal-date-choice">
          {isDesktop && <h5>{t("wybierz_date_rozpoczecia")}</h5>}

          <EADivider />

          {pass.passtype === 8 ? (
            <>
              {isDesktop ? (
                <ClubPassRulesDesktop
                  ids={facilityIds}
                  item={pass}
                  selectedDate={selectedDate}
                  setDateBetween={setDateBetween}
                />
              ) : (
                <ClubPassRulesMobile
                  ids={facilityIds}
                  item={pass}
                  selectedDate={selectedDate}
                  setDateBetween={setDateBetween}
                />
              )}
            </>
          ) : (
            <>
              {isDesktop ? (
                <CalendarPassDesktop
                  ids={facilityIds}
                  selectedDate={selectedDate}
                  setDateBetween={setDateBetween}
                  item={pass}
                />
              ) : (
                <CalendarPassMobile
                  ids={facilityIds}
                  selectedDate={selectedDate}
                  setDateBetween={setDateBetween}
                  item={pass}
                />
              )}
            </>
          )}

          {pass.passtype === 8 && (
            <>
              {!clubCartAgreement && (
                <EACheckbox
                  name="clubCartAgreement"
                  checked={clubCartAgreement}
                  onChange={handleCheckboxChange}
                  label={
                    <span>
                      {t("akceptuje")}{" "}
                      <a href="https://www.parkwodny.pl/fitpark/regulamin/" target="_blank" rel="noreferrer">
                        {t("warunki_korzystania")}
                      </a>{" "}
                      {t("z_karty_klubowej_imiennej_fitpark_open_na_2_miesiace")}.
                    </span>
                  }
                />
              )}

              {hasContinuityIssue && !continuityAgreement && (
                <div style={{ marginTop: clubCartAgreement ? "16px" : "0" }}>
                  <EACheckbox
                    name="continuityAgreement"
                    checked={continuityAgreement}
                    onChange={handleContinuityCheckboxChange}
                    label={
                      <span>
                        W związku z niezachowaniem ciągłości (brak kontynuacji wykupu), zakup kolejnego karnetu
                        spowoduje skrócenie daty ważności karnetu o liczbę dni opóźnienia (max. o połowę okresu
                        ważności). Karnet zostanie skrócony o {daysToDisplay} dni.
                      </span>
                    }
                  />
                </div>
              )}

              {clubCartAgreement && (!hasContinuityIssue || continuityAgreement) && (
                <div className="pass-modal-button">
                  {errorMessage && <EAAlert type="error" message={errorMessage} />}
                  <EAButton title={t("dodaj_do_koszyka")} endIcon={<BsBasket3 />} onClick={onAddToBasket} fullWidth />
                </div>
              )}
            </>
          )}

          {pass.passtype !== 8 && (
            <div className="pass-modal-button">
              {errorMessage && <EAAlert type="error" message={errorMessage} />}
              <EAButton title={t("dodaj_do_koszyka")} endIcon={<BsBasket3 />} onClick={onAddToBasket} fullWidth />
            </div>
          )}
        </div>
      </div>

      <EAModal
        isOpen={readMore}
        title={pass.displayWebName || pass.name}
        onClose={() => setReadMore(false)}
        cancelButtonLabel={t("przeczytalem")}
        hideAcceptButton
      >
        <p dangerouslySetInnerHTML={{ __html: pass.description }}></p>
      </EAModal>
    </EAModal>
  );
};

export default PassModal;
